import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Download,
  Eye,
  File,
  FileImage,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../supabase';
import { getCrmErrorMessage, isCrmSetupError } from '../../utils/crmErrors';

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(bytes / 1024, 1).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function buildSafeFileName(name) {
  return String(name || 'archivo')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-+/g, '-');
}

function isImageMime(mimeType) {
  return String(mimeType || '').startsWith('image/');
}

function fileTone(mimeType) {
  if (isImageMime(mimeType)) return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  if (String(mimeType || '').includes('pdf')) return 'bg-rose-50 border-rose-200 text-rose-700';
  return 'bg-slate-50 border-slate-200 text-slate-600';
}

export default function AttachmentGallery({
  organizationId,
  entityType,
  entityId,
  currentUserId,
  title = 'Adjuntos',
  subtitle = 'Carga secuencial y visualizacion integrada',
  enabled = true,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [setupMessage, setSetupMessage] = useState('');

  const loadAttachments = useCallback(async () => {
    if (!enabled || !organizationId || !entityType || !entityId) {
      setRows([]);
      setSetupMessage('');
      return;
    }

    setLoading(true);
    try {
      const response = await supabase
        .from('crm_entity_attachments')
        .select(`
          id,
          sort_order,
          attachment:attachment_id (
            id,
            bucket_name,
            storage_path,
            original_name,
            mime_type,
            size_bytes,
            created_at
          )
        `)
        .eq('organization_id', organizationId)
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('sort_order', { ascending: true });

      if (response.error) throw response.error;

      const entities = response.data || [];
      const signedRows = await Promise.all(
        entities.map(async (row) => {
          const attachment = row.attachment;
          if (!attachment?.storage_path) {
            return { ...row, signedUrl: null };
          }

          const signed = await supabase.storage
            .from(attachment.bucket_name || 'crm-attachments')
            .createSignedUrl(attachment.storage_path, 60 * 60);

          return {
            ...row,
            signedUrl: signed?.data?.signedUrl || null,
          };
        }),
      );

      setSetupMessage('');
      setRows(signedRows);
    } catch (error) {
      console.error('crm_attachments load failed', error);
      setRows([]);

      if (isCrmSetupError(error)) {
        const message = getCrmErrorMessage(error, 'No se pudieron cargar los adjuntos');
        setSetupMessage(message);
        toast.error(message, { id: 'crm-attachments-setup-error' });
        return;
      }

      setSetupMessage('');
      toast.error(getCrmErrorMessage(error, 'No se pudieron cargar los adjuntos'), { id: 'crm-attachments-load-error' });
    } finally {
      setLoading(false);
    }
  }, [enabled, entityId, entityType, organizationId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  useEffect(() => {
    if (!enabled || !organizationId) return undefined;

    const channel = supabase
      .channel(`crm-attachments-${organizationId}-${entityType}-${entityId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_attachments', filter: `organization_id=eq.${organizationId}` }, loadAttachments)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_entity_attachments', filter: `organization_id=eq.${organizationId}` }, loadAttachments)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled, entityId, entityType, loadAttachments, organizationId]);

  const imageRows = useMemo(
    () => rows.filter((row) => isImageMime(row?.attachment?.mime_type)),
    [rows],
  );

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (!files.length || !organizationId || !entityId) return;

    setUploading(true);
    try {
      for (const [index, file] of files.entries()) {
        const fileName = buildSafeFileName(file.name);
        const storagePath = `${organizationId}/${entityType}/${entityId}/${Date.now()}-${index}-${fileName}`;

        const uploadResponse = await supabase.storage
          .from('crm-attachments')
          .upload(storagePath, file, {
            upsert: false,
            contentType: file.type || undefined,
          });

        if (uploadResponse.error) throw uploadResponse.error;

        const attachmentResponse = await supabase
          .from('crm_attachments')
          .insert({
            organization_id: organizationId,
            bucket_name: 'crm-attachments',
            storage_path: storagePath,
            original_name: file.name,
            mime_type: file.type || 'application/octet-stream',
            size_bytes: file.size || 0,
            uploaded_by: currentUserId || null,
            created_by: currentUserId || null,
            updated_by: currentUserId || null,
          })
          .select()
          .single();

        if (attachmentResponse.error) throw attachmentResponse.error;

        const attachmentId = attachmentResponse.data?.id;
        if (!attachmentId) throw new Error('No se pudo registrar el adjunto');

        const linkResponse = await supabase.from('crm_entity_attachments').insert({
          organization_id: organizationId,
          attachment_id: attachmentId,
          entity_type: entityType,
          entity_id: entityId,
          sort_order: 100 + index,
          created_by: currentUserId || null,
          updated_by: currentUserId || null,
        });

        if (linkResponse.error) throw linkResponse.error;
      }

      setSetupMessage('');
      toast.success('Adjuntos cargados');
      await loadAttachments();
    } catch (error) {
      console.error('crm_attachments upload failed', error);

      if (isCrmSetupError(error)) {
        const message = getCrmErrorMessage(error, 'No se pudieron subir los archivos');
        setSetupMessage(message);
        toast.error(message, { id: 'crm-attachments-setup-error' });
        return;
      }

      toast.error(getCrmErrorMessage(error, 'No se pudieron subir los archivos'), { id: 'crm-attachments-upload-error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (row) => {
    const attachment = row?.attachment;
    if (!row?.id || !attachment?.id) return;

    setDeletingId(row.id);
    try {
      // Cada paso chequea su error: antes se ignoraban las respuestas y el
      // toast de exito salia aunque el borrado hubiera fallado (RLS, red, etc.).
      const linkDeleteResponse = await supabase.from('crm_entity_attachments').delete().eq('id', row.id);
      if (linkDeleteResponse.error) throw linkDeleteResponse.error;

      const attachmentDeleteResponse = await supabase.from('crm_attachments').delete().eq('id', attachment.id);
      if (attachmentDeleteResponse.error) throw attachmentDeleteResponse.error;

      if (attachment.storage_path) {
        const storageDeleteResponse = await supabase.storage
          .from(attachment.bucket_name || 'crm-attachments')
          .remove([attachment.storage_path]);
        if (storageDeleteResponse.error) {
          // El registro ya no existe: avisamos que quedo el archivo huerfano en
          // storage, pero no tratamos el borrado logico como fallido.
          console.error('crm_attachments storage remove failed', {
            bucket: attachment.bucket_name || 'crm-attachments',
            path: attachment.storage_path,
            message: storageDeleteResponse.error?.message || storageDeleteResponse.error,
          });
          toast.error('El adjunto se elimino, pero el archivo no pudo borrarse del almacenamiento.');
        }
      }

      setSetupMessage('');
      toast.success('Adjunto eliminado');
      await loadAttachments();
    } catch (error) {
      console.error('crm_attachments delete failed', error);

      if (isCrmSetupError(error)) {
        const message = getCrmErrorMessage(error, 'No se pudo eliminar el adjunto');
        setSetupMessage(message);
        toast.error(message, { id: 'crm-attachments-setup-error' });
        return;
      }

      toast.error(getCrmErrorMessage(error, 'No se pudo eliminar el adjunto'), { id: 'crm-attachments-delete-error' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="bg-[#0B2A36] px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Paperclip size={16} className="text-[#3FF2AA]" />
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-widest text-white truncate">{title}</p>
            <p className="text-[9px] font-bold uppercase tracking-widest text-white/50">{subtitle}</p>
          </div>
        </div>

        <label className={`rounded-xl text-white px-3 py-2 text-[9px] font-black uppercase tracking-widest flex items-center gap-2 ${setupMessage ? 'bg-white/5 cursor-not-allowed opacity-60' : 'bg-white/10 hover:bg-white/15 cursor-pointer'}`}>
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          Subir
          <input type="file" multiple className="hidden" onChange={handleUpload} disabled={uploading || Boolean(setupMessage)} />
        </label>
      </div>

      <div className="p-4 space-y-4">
        {setupMessage ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <p className="text-[10px] font-black uppercase tracking-widest">Adjuntos no configurados</p>
            <p className="mt-2 text-[11px] font-medium">{setupMessage}</p>
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-slate-400">
            <Loader2 size={18} className="animate-spin mx-auto mb-2" />
            <p className="text-[10px] font-black uppercase tracking-widest">Cargando adjuntos</p>
          </div>
        ) : null}

        {!loading && !setupMessage && rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-slate-400">
            <p className="text-[10px] font-black uppercase tracking-widest">Sin archivos cargados</p>
            <p className="mt-2 text-[10px] font-medium">Subi imagenes, PDFs o planillas para dejar trazabilidad en contexto.</p>
          </div>
        ) : null}

        {!loading && imageRows.length > 0 ? (
          <div>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Vista rapida</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {imageRows.map((row) => (
                <button
                  key={`preview-${row.id}`}
                  type="button"
                  onClick={() => setPreviewItem(row)}
                  className="rounded-2xl border border-slate-200 overflow-hidden bg-slate-50 hover:border-[#51A7FD]/40 transition-colors"
                  title={row?.attachment?.original_name || 'Adjunto'}
                >
                  {row.signedUrl ? (
                    <img
                      src={row.signedUrl}
                      alt={row?.attachment?.original_name || 'Adjunto'}
                      className="w-full h-28 object-cover"
                    />
                  ) : (
                    <div className="h-28 flex items-center justify-center text-slate-400">
                      <FileImage size={24} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {!loading && rows.length > 0 ? (
          <div className="space-y-3">
            {rows.map((row) => {
              const attachment = row?.attachment || {};
              const tone = fileTone(attachment.mime_type);

              return (
                <div key={row.id} className="rounded-2xl border border-slate-200 p-3 bg-slate-50 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-2xl border flex items-center justify-center ${tone}`}>
                      {isImageMime(attachment.mime_type) ? <FileImage size={16} /> : <File size={16} />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-black text-slate-800 truncate">{attachment.original_name || 'Archivo adjunto'}</p>
                      <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                        {attachment.mime_type || 'Archivo'} · {formatBytes(attachment.size_bytes)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {row.signedUrl ? (
                      <a
                        href={row.signedUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-[9px] font-black uppercase tracking-widest text-slate-500 hover:text-[#51A7FD] flex items-center gap-2"
                      >
                        <Eye size={14} />
                        Ver
                      </a>
                    ) : null}
                    {row.signedUrl ? (
                      <a
                        href={row.signedUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-[9px] font-black uppercase tracking-widest text-slate-500 hover:text-[#51A7FD] flex items-center gap-2"
                      >
                        <Download size={14} />
                        Descargar
                      </a>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => handleDelete(row)}
                      disabled={deletingId === row.id}
                      className="rounded-xl border border-rose-200 bg-white px-3 py-2 text-[9px] font-black uppercase tracking-widest text-rose-500 disabled:opacity-50 flex items-center gap-2"
                    >
                      {deletingId === row.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      Quitar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {previewItem?.signedUrl ? (
        <div className="fixed inset-0 z-[90] bg-slate-950/80 backdrop-blur-sm p-4 flex items-center justify-center">
          <button type="button" className="absolute inset-0" onClick={() => setPreviewItem(null)} aria-label="Cerrar vista previa" />
          <div className="relative max-w-5xl w-full rounded-3xl overflow-hidden border border-white/10 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 text-white">
              <p className="text-[11px] font-black uppercase tracking-widest truncate">{previewItem.attachment?.original_name}</p>
              <button type="button" onClick={() => setPreviewItem(null)} className="rounded-xl bg-white/10 p-2 hover:bg-white/15">
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <img
                src={previewItem.signedUrl}
                alt={previewItem.attachment?.original_name || 'Vista previa'}
                className="w-full max-h-[75vh] object-contain rounded-2xl bg-slate-950"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
